// =============================================
// BUDGET CYCLE HELPERS
// =============================================

// Cycles run from the 16th of month N to the 15th of month N+1.
// On/after the 16th: cycle started the 16th of this month.
// Before the 16th:  cycle started the 16th of last month.
export function getCurrentCyclePeriod() {
    const now = new Date();
    const day = now.getDate();
    let cycleYear = now.getFullYear();
    let cycleMonth = now.getMonth(); // 0-indexed

    if (day < 16) {
        // We're in the first half — cycle started in the previous calendar month
        cycleMonth -= 1;
        if (cycleMonth < 0) { cycleMonth = 11; cycleYear -= 1; }
    }

    const pad = n => String(n).padStart(2, '0');
    const cycleId = `${cycleYear}-${pad(cycleMonth + 1)}`;       // "YYYY-MM" of cycle start month
    const cycleStart = `${cycleId}-16`;                           // "YYYY-MM-16"

    // Cycle end is the 15th of the NEXT calendar month after cycleMonth
    let endYear = cycleYear;
    let endMonth = cycleMonth + 1;
    if (endMonth > 11) { endMonth = 0; endYear += 1; }
    const cycleEnd = `${endYear}-${pad(endMonth + 1)}-15`;        // "YYYY-MM-15"

    // Human-readable label e.g. "16 Mar – 15 Apr 2026"
    const startDate = new Date(cycleYear, cycleMonth, 16);
    const endDate = new Date(endYear, endMonth, 15);
    const fmtOpts = { day: 'numeric', month: 'short' };
    const label = `${startDate.toLocaleDateString(undefined, fmtOpts)} – ${endDate.toLocaleDateString(undefined, { ...fmtOpts, year: 'numeric' })}`;

    return { cycleId, cycleStart, cycleEnd, label };
}

// Format "YYYY-MM" cycle ID into a readable label  e.g. "Mar 2026"
export function formatCycleId(cycleId) {
    if (!cycleId) return '';
    const [y, m] = cycleId.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1)
        .toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// Build a full performance report object for a completed cycle
export function buildCyclePerformanceReport(cycleId, cycleStart, cycleEnd, label, groups) {
    const fmt2 = n => Math.round((n || 0) * 100) / 100;
    const now = new Date().toISOString();

    const serialiseGroup = (items, symbol) =>
        (items || []).filter(i => i.enabled && (i.amount || 0) > 0).map(i => {
            const budget = parseFloat(i.amount) || 0;
            const spent = i.actualSpent || 0;
            const pct = budget > 0 ? fmt2((spent / budget) * 100) : 0;
            const status = pct > 100 ? 'over' : pct >= 90 ? 'near' : 'ok';
            return {
                category: i.category || '',
                description: i.description || '',
                budget: fmt2(budget),
                spent: fmt2(spent),
                remaining: fmt2(budget - spent),
                pct,
                status,
                spendLog: (i.spendLog || []).map(e => ({ date: e.date, amount: fmt2(e.amount), note: e.note || '' })),
            };
        });

    const groupTotals = (items) => {
        const enabled = (items || []).filter(i => i.enabled && (i.amount || 0) > 0);
        return {
            budget: fmt2(enabled.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)),
            spent: fmt2(enabled.reduce((s, i) => s + (i.actualSpent || 0), 0)),
        };
    };

    const g = groups; // { sgCycle1, sgCycle2, lkA, lkB }
    return {
        reportType: 'iPlanner-BudgetCycle-Report',
        cycleId,
        cycleStart,
        cycleEnd,
        label,
        exportedAt: now,
        sgCycle1: { items: serialiseGroup(g.sgCycle1, '$'), ...groupTotals(g.sgCycle1) },
        sgCycle2: { items: serialiseGroup(g.sgCycle2, '$'), ...groupTotals(g.sgCycle2) },
        lkA: { items: serialiseGroup(g.lkA, '₨'), ...groupTotals(g.lkA) },
        lkB: { items: serialiseGroup(g.lkB, '₨'), ...groupTotals(g.lkB) },
        sgTotal: fmt2(groupTotals(g.sgCycle1).spent + groupTotals(g.sgCycle2).spent),
        lkTotal: fmt2(groupTotals(g.lkA).spent + groupTotals(g.lkB).spent),
    };
}
